import { CLIENT_PHOTO_MAX_BYTES, validateClientPhotoUpload, validateClientPhotoSelection, clientPhotoMime } from './clientPhotoUploadPolicy';

const decodePhoto = async file => {
  if (typeof createImageBitmap === 'function') return createImageBitmap(file);
  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Unable to decode photo.'));
      image.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
};

const compressPhoto = async file => {
  if (file.size <= CLIENT_PHOTO_MAX_BYTES) return file;
  let bitmap;
  const canvas = document.createElement('canvas');
  try {
    bitmap = await decodePhoto(file);
    const width = bitmap.width;
    const height = bitmap.height;
    if (!width || !height) throw new Error('Invalid dimensions.');
    const scale = Math.min(1, 2560 / Math.max(width, height));
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Image processing is unavailable.');
    for (let attempt = 0; attempt < 8; attempt += 1) {
      const reduction = scale * (0.8 ** attempt);
      canvas.width = Math.max(1, Math.round(width * reduction));
      canvas.height = Math.max(1, Math.round(height * reduction));
      context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      // Keep PNG alpha; JPEG uses a bounded quality reduction as dimensions shrink.
      const blob = await new Promise(resolve => canvas.toBlob(resolve, file.type, Math.max(0.65, 0.9 - attempt * 0.05)));
      if (!blob || blob.type !== file.type) throw new Error('Unable to encode photo.');
      if (blob.size > 0 && blob.size <= CLIENT_PHOTO_MAX_BYTES) {
        return new File([blob], file.name, { type: file.type, lastModified: file.lastModified });
      }
    }
    throw new Error('Photo is still too large.');
  } catch (error) {
    throw new Error(`Could not reduce "${file.name}" to 2 MB. Choose a smaller JPEG or PNG photo and try again.`);
  } finally {
    if (bitmap && typeof bitmap.close === 'function') bitmap.close();
    canvas.width = 0;
    canvas.height = 0;
  }
};

export const prepareClientPhotos = async files => {
  const selectionError = validateClientPhotoSelection(files);
  if (selectionError) throw new Error(selectionError);
  // Decode sequentially so a multi-photo selection does not allocate many canvases at once.
  const prepared = [];
  for (const file of files) {
    const type = clientPhotoMime(file);
    const normalized = type === file.type ? file : new File([file], file.name, {type, lastModified:file.lastModified});
    prepared.push(await compressPhoto(normalized));
  }
  const validationError = validateClientPhotoUpload(prepared);
  if (validationError) throw new Error(validationError);
  return prepared;
};
